// Fallback propio de los parámetros de matching de Meta (_fbc/_fbp), siguiendo
// las best practices del "Parameter Builder" oficial: si el script del píxel no
// corre (ad blocker, error de red), el fbclid de la URL se perdería aunque esté
// ahí — acá lo convertimos nosotros a _fbc con el formato oficial, y generamos
// _fbp si falta. NUNCA pisa cookies existentes salvo que llegue un fbclid nuevo
// (último click manda), y no normaliza el valor (_fbc es case-sensitive).
//
// Núcleo puro (`resolverMetaParams`, testeado en vitest env node) + adaptador
// de browser (`ensureMetaParams`) que corre al boot de la app y es best-effort
// total: jamás lanza.

export interface MetaParamsInput {
  /** window.location.search (con o sin '?'). */
  search: string;
  fbcCookie: string | null;
  fbpCookie: string | null;
  now: number;
  /** Generador del random de _fbp (inyectable para tests). */
  random: () => number;
  /** 'com'=0, 'estudio-atlas.com'=1, 'app.estudio-atlas.com'=2 — según dónde vive la cookie. */
  subdomainIndex: number;
}

/** fbclid embebido en un _fbc (`fb.N.time.<fbclid>`; el fbclid puede traer puntos). */
function fbclidDeFbc(fbc: string): string | null {
  const partes = fbc.split('.');
  return partes.length >= 4 ? partes.slice(3).join('.') : null;
}

/** Decide qué cookies hay que setear. Devuelve solo lo que falta o cambió. */
export function resolverMetaParams(input: MetaParamsInput): { fbc?: string; fbp?: string } {
  const out: { fbc?: string; fbp?: string } = {};
  const fbclid = new URLSearchParams(input.search).get('fbclid');
  if (fbclid && (!input.fbcCookie || fbclidDeFbc(input.fbcCookie) !== fbclid)) {
    out.fbc = `fb.${input.subdomainIndex}.${input.now}.${fbclid}`;
  }
  if (!input.fbpCookie) {
    out.fbp = `fb.${input.subdomainIndex}.${input.now}.${input.random()}`;
  }
  return out;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Dominio donde setear las cookies, imitando al píxel: el eTLD+1 naive
 * (`.estudio-atlas.com`) para que landing y app las compartan. En localhost /
 * IPs no se especifica dominio.
 */
function dominioCookie(hostname: string): string | null {
  const partes = hostname.split('.');
  if (partes.length < 2 || /^[0-9.]+$/.test(hostname)) return null;
  return '.' + partes.slice(-2).join('.');
}

const NOVENTA_DIAS_S = 90 * 24 * 60 * 60;

function setCookie(name: string, value: string): void {
  const dominio = dominioCookie(window.location.hostname);
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${NOVENTA_DIAS_S}; SameSite=Lax`;
  if (dominio) cookie += `; domain=${dominio}`;
  if (window.location.protocol === 'https:') cookie += '; Secure';
  document.cookie = cookie;
}

/**
 * Corre al boot de la app (y al montar las pantallas de alta, vía
 * captureAttributionParams): asegura _fbc/_fbp y las espeja en localStorage
 * (`atlas:fbc`/`atlas:fbp`, las mismas claves que lee saveSignupAttribution)
 * para que sobrevivan si el navegador borra las cookies (ITP de Safari).
 */
export function ensureMetaParams(): void {
  try {
    // El subdomainIndex refleja dónde se DEFINE la cookie ('com'=0,
    // 'estudio-atlas.com'=1, ...): la seteamos en el eTLD+1, así que es la
    // cantidad de partes de ese dominio menos 1 (en localhost/IP queda 0).
    const dominio = dominioCookie(window.location.hostname);
    const resuelto = resolverMetaParams({
      search: window.location.search,
      fbcCookie: getCookie('_fbc'),
      fbpCookie: getCookie('_fbp'),
      now: Date.now(),
      random: () => Math.floor(Math.random() * 2147483647),
      subdomainIndex: dominio ? dominio.replace(/^\./, '').split('.').length - 1 : 0,
    });
    if (resuelto.fbc) setCookie('_fbc', resuelto.fbc);
    if (resuelto.fbp) setCookie('_fbp', resuelto.fbp);
    const fbc = resuelto.fbc ?? getCookie('_fbc');
    const fbp = resuelto.fbp ?? getCookie('_fbp');
    try {
      if (fbc) localStorage.setItem('atlas:fbc', fbc);
      if (fbp) localStorage.setItem('atlas:fbp', fbp);
    } catch {}
  } catch {}
}
